import * as React from 'react';
import { Alert, Chip, Link as MuiLink, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AI_LABELS, AiTaskExecutionsFilter, getAiTaskExecutions } from '../../Api';
import { AI_TASK_EXECUTION_STATUSES, AiTaskExecutionStatus, AiTaskExecutionSummaryDto } from '../../models/dtos/AiDto';
import { aiLoadErrorText, AiUsageStatusChip, EnumSelect, formatAiCount, formatAiDate, formatAiMs, formatAiUsd } from './AiShared';

export function TaskExecutionLink({ executionId, label }: { executionId: string; label?: string }) {
    return (
        <MuiLink component={RouterLink} to={`/ai/task-executions/${encodeURIComponent(executionId)}`} underline="hover"
            variant="caption" sx={{ fontFamily: label ? undefined : 'monospace' }}>
            {label ?? executionId.slice(0, 8) + '…'}
        </MuiLink>
    );
}

/** Initial filter values, e.g. from the page's query string. */
export type AiTaskExecutionsInitial = Pick<AiTaskExecutionsFilter, 'taskCode' | 'status' | 'errorCode' | 'endUserId' | 'runId' | 'from' | 'to'>;

/** Paged task executions (server paging), newest first. With `fixedTaskCode` the task filter is hidden. */
export default function AiTaskExecutionsGrid({ fixedTaskCode, initial = {}, reloadKey = 0, pageSize = 25 }: {
    fixedTaskCode?: string;
    initial?: AiTaskExecutionsInitial;
    reloadKey?: number;
    pageSize?: number;
}) {
    const navigate = useNavigate();
    const [taskCode, setTaskCode] = React.useState(initial.taskCode ?? '');
    const [status, setStatus] = React.useState<AiTaskExecutionStatus | ''>((initial.status as AiTaskExecutionStatus) ?? '');
    const [errorCode, setErrorCode] = React.useState(initial.errorCode ?? '');
    const [endUserId, setEndUserId] = React.useState(initial.endUserId ?? '');
    const [runId, setRunId] = React.useState(initial.runId ?? '');
    const [from, setFrom] = React.useState(initial.from ?? '');
    const [to, setTo] = React.useState(initial.to ?? '');
    const [filter, setFilter] = React.useState<AiTaskExecutionsFilter | null>(null);
    const [rows, setRows] = React.useState<AiTaskExecutionSummaryDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize });

    React.useEffect(() => {
        const timer = window.setTimeout(() => setFilter({
            taskCode: fixedTaskCode ?? (taskCode.trim() || undefined),
            status: status || undefined,
            errorCode: errorCode.trim().toUpperCase() || undefined,
            endUserId: endUserId || undefined,
            runId: runId.trim() || undefined,
            from: from || undefined,
            to: to || undefined,
        }), 400);
        return () => window.clearTimeout(timer);
    }, [fixedTaskCode, taskCode, status, errorCode, endUserId, runId, from, to]);

    React.useEffect(() => { setPaginationModel(p => (p.page === 0 ? p : { ...p, page: 0 })); }, [filter]);

    const load = React.useCallback(async () => {
        if (filter == null) return;
        setLoading(true);
        const page = await getAiTaskExecutions(filter, paginationModel.page * paginationModel.pageSize, paginationModel.pageSize);
        setLoading(false);
        if (page == null) {
            setError(aiLoadErrorText(AI_LABELS.taskExecutions, 'Failed to load task executions.'));
            setRows([]);
            setRowCount(0);
            return;
        }
        setError(null);
        setRows(page.items ?? []);
        setRowCount(page.totalItems ?? 0);
    }, [filter, paginationModel]);

    React.useEffect(() => { load(); }, [load, reloadKey]);

    const columns: GridColDef<AiTaskExecutionSummaryDto>[] = [
        { field: 'createdAt', headerName: 'At', width: 165, sortable: false, valueFormatter: ({ value }) => formatAiDate(value as string) },
        ...(fixedTaskCode ? [] : [{ field: 'taskCode', headerName: 'Task', width: 190, sortable: false } as GridColDef<AiTaskExecutionSummaryDto>]),
        {
            field: 'status', headerName: 'Status', width: 150, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={0.25} alignItems="flex-start">
                    <AiUsageStatusChip status={row.status} />
                    {row.errorCode && <Typography variant="caption" color="error">{row.errorCode}</Typography>}
                    {row.stopReason && row.stopReason !== 'END' && <Typography variant="caption" color="text.secondary">stop {row.stopReason}</Typography>}
                    {row.cached && <Chip size="small" variant="outlined" label="cached" />}
                </Stack>
            ),
        },
        { field: 'caller', headerName: 'Caller', width: 140, sortable: false },
        {
            field: 'endUser', headerName: 'End user / subject', width: 160, sortable: false,
            renderCell: ({ row }) => (
                <Stack>
                    {row.endUserId != null && <Typography variant="body2">#{row.endUserId}</Typography>}
                    {row.subjectType && <Typography variant="caption" color="text.secondary">{row.subjectType} / {row.subjectId}</Typography>}
                    {row.endUserId == null && !row.subjectType && '—'}
                </Stack>
            ),
        },
        {
            field: 'outputPreview', headerName: 'Output', flex: 1, minWidth: 260, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={0.25} sx={{ minWidth: 0 }}>
                    {row.error && <Typography variant="caption" color="error" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.error}</Typography>}
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {row.outputPreview ?? '—'}
                    </Typography>
                </Stack>
            ),
        },
        {
            field: 'tokens', headerName: 'Tokens in / out', width: 130, sortable: false,
            renderCell: ({ row }) => (
                <Tooltip title={row.modelKey ?? ''}>
                    <Typography variant="body2">{formatAiCount(row.inputTokens)} / {formatAiCount(row.outputTokens)}</Typography>
                </Tooltip>
            ),
        },
        { field: 'costUsd', headerName: 'Cost', width: 100, sortable: false, valueFormatter: ({ value }) => formatAiUsd(value as number) },
        { field: 'latencyMs', headerName: 'Latency', width: 90, sortable: false, valueFormatter: ({ value }) => formatAiMs(value as number) },
    ];

    return (
        <Stack gap={1.5}>
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
                {!fixedTaskCode && <TextField size="small" label="Task code" value={taskCode} onChange={e => setTaskCode(e.target.value)} />}
                <EnumSelect size="small" label="Status" value={status} options={AI_TASK_EXECUTION_STATUSES} allowEmpty fullWidth={false}
                    sx={{ minWidth: 140 }} onChange={setStatus} />
                <TextField size="small" label="Error code" value={errorCode} placeholder="OUTPUT_INVALID" onChange={e => setErrorCode(e.target.value)} />
                <TextField size="small" label="End user id" value={endUserId} onChange={e => setEndUserId(e.target.value.replace(/\D/g, ''))} />
                <TextField size="small" label="Agent run id" value={runId} onChange={e => setRunId(e.target.value)} />
                <TextField size="small" type="date" label="From" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="To" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Stack>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                getRowId={row => row.executionId}
                getRowHeight={() => 'auto'}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                onRowClick={({ row }) => navigate(`/ai/task-executions/${encodeURIComponent((row as AiTaskExecutionSummaryDto).executionId)}`)}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-cell': { py: 1 } }}
                localeText={{ noRowsLabel: 'No task executions' }}
            />
        </Stack>
    );
}
